import React, { useState, useEffect } from 'react'
import { FiPlay, FiTrash2, FiFilter, FiClock, FiSearch, FiMusic, FiHeadphones } from 'react-icons/fi'

interface PlaylistsProps {
  accessToken: string | null
  onPlay: (playlistId: string) => void
  onDelete: (playlistId: string) => void
  defaultFilter?: 'all' | 'mood' | 'own'
  hideFilters?: boolean
  selectedPlaylistId?: string | null
  shouldRefresh?: boolean
  onRefreshDone?: () => void
}

interface Playlist {
  id: string
  name: string
  images: { url: string }[]
  tracks: { total: number }
  owner: { id: string }
}

const Playlists: React.FC<PlaylistsProps> = ({ 
  accessToken,
  onPlay, 
  onDelete, 
  defaultFilter = 'all',
  hideFilters = false,
  selectedPlaylistId = null,
  shouldRefresh = false,
  onRefreshDone
}) => {
  const [playlists, setPlaylists] = useState<Playlist[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<'all' | 'mood' | 'own'>(defaultFilter)
  const [searchTerm, setSearchTerm] = useState('')

  const fetchPlaylists = async () => {
    if (!accessToken) {
      setError('No Spotify access token available');
      setLoading(false);
      return;
    }
    
    try {
      // Set loading state
      setLoading(true)
      
      let allPlaylists: any[] = [];
      let nextUrl: string | null = 'https://api.spotify.com/v1/me/playlists?limit=50';
      let pageCount = 0;
      
      // Implement pagination to get all playlists
      while (nextUrl) {
        console.log(`Fetching playlist page ${pageCount + 1}, URL: ${nextUrl}`);
        
        const response: Response = await fetch(nextUrl, {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        });
        
        if (response.status === 401) {
          console.error('Access token expired or invalid, need to re-authenticate');
          localStorage.removeItem('spotify_access_token');
          setError('Authentication expired. Please login again.');
          setLoading(false);
          return;
        }
        
        if (!response.ok) {
          const errorText = await response.text().catch(() => 'Unknown error');
          console.error(`Spotify API error: ${response.status}`, errorText);
          throw new Error(`Spotify API error: ${response.status}`);
        }
        
        const data: {
          items: any[];
          next: string | null;
          total: number;
          offset: number;
          limit: number;
        } = await response.json();
        
        if (!data.items || !Array.isArray(data.items)) {
          console.error('Invalid response format', data);
          throw new Error('Invalid response format');
        }
        
        console.log(`Received ${data.items.length} playlists (page ${pageCount + 1}), total: ${data.total}, offset: ${data.offset}, limit: ${data.limit}`);
        
        // Add current page of playlists to our collection
        allPlaylists = [...allPlaylists, ...data.items];
        pageCount++;
        
        // Check if there's another page
        nextUrl = data.next;
        
        // Safety check to prevent infinite loops
        if (pageCount > 10) {
          console.warn('Stopping playlist pagination after 10 pages as a safety measure');
          break;
        }
      }
      
      console.log(`Total playlists fetched: ${allPlaylists.length} in ${pageCount} pages`);
      
      // Add defensive checks for each playlist item
      const userPlaylists = allPlaylists.map((playlist: any) => {
        // Ensure each playlist has the required fields
        return {
          id: playlist.id || 'unknown-id',
          name: playlist.name || 'Unnamed Playlist',
          images: Array.isArray(playlist.images) ? playlist.images : [],
          tracks: playlist.tracks || { total: 0 },
          owner: playlist.owner || { id: 'unknown' }
        };
      });
      
      console.log(`Processed ${userPlaylists.length} playlists successfully`); 
      
      setPlaylists(userPlaylists)
      setError(null)
    } catch (err) {
      console.error('Error fetching playlists:', err)
      setError('Failed to load playlists. Please try refreshing the page or logging in again.')
    } finally {
      setLoading(false)
      // Call the onRefreshDone callback if provided
      if (onRefreshDone) {
        onRefreshDone();
      }
    }
  };

  useEffect(() => {
    // Fetch playlists on component mount if we have an access token
    if (accessToken) {
      fetchPlaylists();
    }
  }, [accessToken]);

  // Handle the shouldRefresh prop to trigger playlist refresh
  useEffect(() => {
    if (shouldRefresh && accessToken) {
      fetchPlaylists();
    }
  }, [shouldRefresh, accessToken]);

  const filteredPlaylists = playlists.filter(playlist => {
    const matchesSearch = playlist.name.toLowerCase().includes(searchTerm.toLowerCase())
    const userId = localStorage.getItem('spotify_user_id')
    
    switch (filter) {
      case 'mood':
        return matchesSearch && playlist.name.toLowerCase().includes('mood')
      case 'own':
        return matchesSearch && playlist.owner.id === userId
      default:
        return matchesSearch
    }
  })

  if (loading) return (
    <div className="w-full">
      <div className="bg-spotify-light rounded-lg p-6 shadow-lg">
        <div className="flex flex-col justify-center items-center py-10">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-spotify-green mb-4"></div>
          <span className="text-spotify-text-gray font-medium">Loading your playlists...</span>
          <span className="text-xs text-spotify-text-gray mt-2">This may take a moment if you have many playlists</span>
          <div className="mt-4 w-48 h-1 bg-spotify-dark rounded-full overflow-hidden">
            <div className="h-full bg-spotify-green animate-pulse"></div>
          </div>
        </div>
      </div>
    </div>
  )

  if (error) return (
    <div className="w-full">
      <div className="bg-spotify-light rounded-lg p-6 shadow-lg">
        <div className="text-center py-10">
          <div className="text-spotify-error text-lg mb-4">{error}</div>
          <button 
            onClick={() => fetchPlaylists()}
            className="px-4 py-2 bg-spotify-green rounded-lg text-white hover:bg-opacity-90 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <div className="w-full">
      <div className="bg-spotify-light rounded-lg p-4 md:p-6 shadow-lg">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-0 sm:justify-between">
          <div className="relative w-full sm:flex-1">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
              <FiSearch className="text-spotify-text-gray" />
            </div>
            <input
              type="text"
              placeholder="Search playlists..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-spotify-input text-spotify-text-light pl-10 pr-4 py-2 rounded-lg border border-spotify-border focus:outline-none focus:border-spotify-green"
            />
          </div>
          
          {!hideFilters && (
            <div className="w-full sm:w-auto sm:ml-4">
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as 'all' | 'mood' | 'own')}
                className="w-full bg-spotify-input text-spotify-text-light px-3 py-2 rounded-lg border border-spotify-border focus:outline-none focus:border-spotify-green"
              >
                <option value="all">All Playlists</option>
                <option value="mood">Mood Playlists</option>
                <option value="own">My Playlists</option>
              </select>
            </div>
          )}
        </div>

        {filteredPlaylists.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-spotify-text-gray">
            <svg className="w-16 h-16 mb-4 text-spotify-text-gray opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <p className="text-lg">No playlists found</p>
            <p className="text-sm mt-2">Try adjusting your search</p>
          </div>
        ) : (
          <>
            <div className="mb-4 text-sm text-spotify-text-gray">
              Showing {filteredPlaylists.length} {filteredPlaylists.length === 1 ? 'playlist' : 'playlists'}
              {filter !== 'all' && ` (filtered)`}
              {searchTerm && ` matching "${searchTerm}"`}
              {playlists.length > 0 && ` of ${playlists.length} total`}
            </div>
            <div className="overflow-y-auto overflow-x-hidden" style={{ 
              maxHeight: "calc(50vh - 100px)",
              msOverflowStyle: "none",
              scrollbarWidth: "none"
            }}>
              <style>
                {`
                  div.overflow-y-auto::-webkit-scrollbar {
                    display: none;
                  }
                `}
              </style>
              <ul className="list-none">
                {filteredPlaylists.map(playlist => (
                  <li key={playlist.id} className="mb-4">
                    <div 
                      className={`
                        p-3 bg-spotify-light rounded-lg shadow-sm hover:shadow-md transition-shadow relative
                        ${selectedPlaylistId === playlist.id ? 'border-2 border-spotify-green' : ''}
                      `}
                    >
                      <div className="flex flex-col sm:flex-row items-center">
                        {/* Playlist Image/Art */}
                        <div className="h-16 w-16 flex-shrink-0 mb-2 sm:mb-0 sm:mr-3">
                          {playlist.images && playlist.images.length > 0 ? (
                            <img 
                              src={playlist.images[0].url} 
                              alt={playlist.name} 
                              className="h-full w-full object-cover rounded-md" 
                            />
                          ) : (
                            <div className="h-full w-full bg-spotify-dark flex items-center justify-center rounded-md">
                              <FiMusic className="text-spotify-text-gray" />
                            </div>
                          )}
                        </div>
                        
                        {/* Playlist Info */}
                        <div className="flex-grow flex flex-col items-center sm:items-start mb-2 sm:mb-0 w-full">
                          <h3 className={`font-semibold truncate w-full text-center sm:text-left max-w-full px-2 sm:px-0 ${selectedPlaylistId === playlist.id ? 'text-spotify-green' : ''}`}>
                            {playlist.name}
                          </h3>
                          <p className="text-xs text-spotify-text-gray">
                            {playlist.tracks?.total || 0} tracks
                          </p>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex space-x-2">
                          <button
                            onClick={() => onPlay(playlist.id)}
                            className={`
                              p-2 rounded-full hover:bg-spotify-input transition-colors
                              ${selectedPlaylistId === playlist.id ? 'text-spotify-green' : ''}
                            `}
                            title="Play"
                          >
                            {selectedPlaylistId === playlist.id ? (
                              <FiHeadphones className="text-spotify-green" />
                            ) : (
                              <FiPlay />
                            )}
                          </button>
                          {playlist.owner.id === localStorage.getItem('spotify_user_id') && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (confirm('Are you sure you want to delete this playlist?')) {
                                  onDelete(playlist.id);
                                }
                              }}
                              className="p-2 rounded-full hover:bg-spotify-input transition-colors text-spotify-text-gray hover:text-spotify-error"
                              title="Delete"
                            >
                              <FiTrash2 />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default Playlists 